<template>
  <div class="table-wrapper tenancy-other-contacts">
    <div class="table-caption">
      {{ $t('tenancy.table.allContacts') }}
    </div>
    <div
      v-if="allowLinkContacts"
      class="link__contact"
    >
      <button
        type="button"
        class="btn link__contact--btn"
        :disabled="!tenancyId"
        @click="linkFunc({isOthers: true})"
      >{{ $t('tenancy.table.linkContact') }}</button>
      <span class="link__contact--plus">+</span>
    </div>
    <table>
      <thead>
        <tr>
          <th
            v-for="(header, index) in itemHeaders"
            :key="index"
            :class="header.class"
          >{{ header.name }}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(item, index) in otherContacts"
          v-show="enableEcircularFeature || (!deep(item, 'CompanyContact.isCircular') || deep(item, 'CompanyContact.isEmergency'))"
          :key="index"
        >
          <td class="number-column">
            <div v-if="canAccessContact">
              <a
                class="name-hyper-link"
                @click="navToContactDetail(item.id, true)"
              >{{ displayI18nFullName(item.firstName, item.lastName) || item.name }}</a>
              <i
                v-if="isEngineer"
                v-show="!!item.remarks"
                class="remark-included-flag"
              />
            </div>
            <div v-else>
              {{ displayI18nFullName(item.firstName, item.lastName) || item.name }}
            </div>
          </td>
          <td>{{ deep(item, 'CompanyContact.title') | isEmptyValue }}</td>
          <td>{{ item.phone | isEmptyValue }}</td>
          <td>{{ item.email | isEmptyValue }}</td>
          <td>{{ calcContactRoleType(item) }}</td>
          <td>
            <template v-for="(mapItem, mapIndex) in remarksFieldMap">
              <TextField
                v-if="mapItem.shouldShow(item)"
                :key="mapIndex"
                v-hkldinput
                parentNode=""
                :value="mapItem.componentVal(item)"
                :disabled="mapItem.shouldDisable()"
                :readonly="!allowLinkContacts"
                @updateValue="val => mapItem.updateValueFn(index, val)"
              />
            </template>
          </td>
          <td class="icon-remove">
            <i
              v-if="allowLinkContacts"
              class="fa fa-times-thin"
              @click="openDeleteConfirm(index, item)"
            />
          </td>
        </tr>
      </tbody>
    </table>
    <ActionConfirmModal
      ref="deleteConfirmModal"
      :modalId="delConfModalId"
      :confirmTitle="$t('deleteConfirmMsg')"
    />
  </div>
</template>

<script>
import { mapMutations, mapActions } from 'vuex'
import lodash from 'lodash'
import { unref, computed, getCurrentInstance } from 'vue-demi'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import TextField from '@/controls/TextField'
import { aclHelper, ccdPermsTypes } from '@/utils/aclHelper'
import { tenancyType } from '@/utils/locale'
import mutationTypes from '@/store/mutation-types'
import notification from '@/utils/notification'
import { utils as formUtils } from '@/plugin/InputChange'
import { isLastLink } from '@/api/contacts/request'
import formFullnameMixin from '@/pages/formFullnameMixin'
import { useStoreGetters } from '@/store/index'
import { useRouteParamId } from '@/router/useRouter'
import { TENANCY_ALL_CONTACT_TYPES } from '../consts'
import { calcContactRoleType } from '../utils'
import navToContactDetailMixin from './navToContactDetailMixin'
import { useTrContactTabRemarks } from '../hook'

export default {
  name: 'OtherContact',
  components: {
    TextField,
    ActionConfirmModal,
  },
  mixins: [navToContactDetailMixin, formFullnameMixin],
  props: {
    allowLinkContacts: Boolean,
    linkFunc: {
      type: Function,
      default: () => {},
    },
  },
  setup (props) {
    const { proxy } = getCurrentInstance()
    const tenancyId = useRouteParamId()
    const extraContactFieldNames = TENANCY_ALL_CONTACT_TYPES.filter(type => type !== 'isOthers')
    const { enableEcircularFeature } = useStoreGetters('regions', ['enableEcircularFeature'])
    const { currentTenancy } = useStoreGetters('ccd', ['currentTenancy'])
    const { isEngineer } = useStoreGetters('user', ['isEngineer'])
    const isRetailTr = computed(() => {
      return unref(currentTenancy).type === tenancyType.Retail
    })
    const canEditMarketing = computed(() => {
      return aclHelper.getAccess(ccdPermsTypes.tenancy.manageMarketingContacts)
    })
    const updateRemarks = (index, val) => {
      proxy.updatePathField(index, 'oRemarks', val)
    }
    const updateFireMarshalRemarks = (index, val) => {
      proxy.updatePathField(index, 'fRemarks', val)
    }
    const updateMarketingRemarks = (index, val) => {
      proxy.updatePathField(index, 'mRemarks', val)
    }
    const remarksFieldMap = useTrContactTabRemarks({
      isRetailTr,
      props,
      canEditMarketing,
      updateRemarks,
      updateFireMarshalRemarks,
      updateMarketingRemarks,
    })
    return {
      canEditMarketing,
      remarksFieldMap,
      updateRemarks,
      updateFireMarshalRemarks,
      updateMarketingRemarks,
      tenancyId,
      extraContactFieldNames,
      currentTenancy,
      enableEcircularFeature,
      isRetail: isRetailTr,
      calcContactRoleType,
      isEngineer,
    }
  },
  data () {
    return {
      delConfModalId: `allCont-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`,
      deep,
      workingHourPositions: [],
      nonWorkingHourPositions: [],
      isLastLinkMap: {},
      afterLinkAction: false,
    }
  },
  computed: {
    itemHeaders () {
      return [{
        name: this.$t('tenancy.table.name'),
        class: 'column-name',
      }, {
        name: this.$t('company.contactTable.title'),
        class: 'column-phone',
      }, {
        name: this.$t('tenancy.table.priPhone'),
        class: 'column-phone',
      }, {
        name: this.$t('tenancy.table.priEmail'),
        class: 'column-email',
      }, {
        name: this.$t('company.contactTable.roleType'),
        class: 'column-email',
      }, {
        name: this.$t('contact.remarks'),
        class: 'column-remarks',
      }]
    },
    otherContacts () {
      if (!(this.currentTenancy && this.currentTenancy.tenancyContacts)) return []

      return this.currentTenancy.tenancyContacts.reduce((partial, item, index) => {
        if (item.CompanyContact.isOthers) {
          partial[index] = item
        }
        return partial
      }, {})
    },
  },
  async mounted () {
    if (this.allowLinkContacts) {
      const routeParams = this.$router.currentRoute.params
      this.afterLinkAction = routeParams.afterLinkAction || false
      if (!this.currentTenancy.tenancyContacts) {
        await this.loadTenancy(this.tenancyId)
      }
      const ids = this.currentTenancy.tenancyContacts.map(item => item.id)
      this.isLastLinkMap = await isLastLink({ids})
    }
  },
  methods: {
    ...mapActions('ccd', ['loadTenancy']),
    ...mapMutations('ccd', {
      updateTenancyContactPathField: mutationTypes.TENANCY_CONTACT_UPDATE_PATH_FIELD,
    }),
    updatePathField (index, fieldName, newValue) {
      this.updateTenancyContactPathField({
        index,
        path: [fieldName],
        newValue,
      })
      formUtils.setDirty()
    },
    openDeleteConfirm (index, item) {
      this.$refs.deleteConfirmModal.open(this.removeContact, [index, item])
    },
    removeContact (index, item) {
      const cantUnlink = this.extraContactFieldNames.some(fieldName => lodash.get(item, `CompanyContact.${fieldName}`))
      if (cantUnlink) {
        notification.fireNotification(false, this.$t('hint.contact.noUnlinkAll'))
        return
      }
      this.updatePathField(index, 'isOthers', false)
      if (!this.afterLinkAction && this.isLastLinkMap[item.id]) {
        this.removeContactFromSystem(index, item)
      }
    },
    removeContactFromSystem (index, item) {
      this.updatePathField(index, 'delete', true)
    },
  },
}
</script>
<style lang="scss" scoped>
@import '~styles/tender/view';
@import '~styles/table';
@import '~styles/components/tenancy-contacts';
</style>

<style lang="scss" scoped>
.tenancy-other-contacts {
  .column-remarks {
    width: 34%;
  }
  .text-input textarea {
    width: 100%;
  }
  th {
    white-space: nowrap;
  }
}
</style>
